// The person's own arrangement of Work's sidebar (M2-07): FAVORITES (starred spaces and boards;
// on the server since WOS-BUNDLE, favorites.ts) and the SPACES tree's groups and order (Work OS sidebar: a group is a folder of spaces; drag a
// space into a group, out of it, or before another space). The M2 contracts give spaces no
// parent, order or favourite, so this is per person and per browser, like the sidebar's width:
// `localStorage companyos.work.{favorites|tree}.{companyId}`. Nothing here changes what anyone
// else sees. The rules are pure and unit-tested; `useArrangement` persists them.
import { useCallback, useEffect, useState } from "react";

/** DOCS-KB: a starred doc is a `page` favorite (FAVORITES and the Docs screen's Starred view).
 * WOS-BUNDLE: tasks and channels are starred too, and the list lives on the server (favorites.ts). */
export type Favorite = { kind: "space" | "board" | "page" | "task" | "channel"; id: string };
export type SpaceGroup = { id: string; name: string; spaceIds: string[] };
/** Top-level order: group ids (`g:…`) and ungrouped space ids. */
export type Tree = { groups: SpaceGroup[]; order: string[] };

export const EMPTY_TREE: Tree = { groups: [], order: [] };

export type TreeRow =
  | { kind: "group"; group: SpaceGroup }
  | { kind: "space"; spaceId: string; groupId: string | null };

/** The tree's top level for these spaces: arranged ones in their order, the others after them in
 * the order given (BOARD-ORDER: the company's order of spaces), groups wherever they were put;
 * spaces that no longer exist drop out. */
export function arrange(tree: Tree, spaceIds: readonly string[]) {
  const exists = new Set(spaceIds);
  const groups = tree.groups.map((g) => ({
    ...g,
    spaceIds: g.spaceIds.filter((id) => exists.has(id)),
  }));
  const grouped = new Set(groups.flatMap((g) => g.spaceIds));
  const placed = new Set(tree.order);
  const top: TreeRow[] = [];
  for (const key of tree.order) {
    const group = groups.find((g) => g.id === key);
    if (group) top.push({ kind: "group", group });
    else if (exists.has(key) && !grouped.has(key))
      top.push({ kind: "space", spaceId: key, groupId: null });
  }
  for (const g of groups) if (!placed.has(g.id)) top.push({ kind: "group", group: g });
  const fresh = spaceIds.filter((id) => !placed.has(id) && !grouped.has(id));
  for (const id of fresh) top.push({ kind: "space", spaceId: id, groupId: null });
  return top;
}

const topOrder = (tree: Tree, spaceIds: readonly string[]) =>
  arrange(tree, spaceIds).map((r) => (r.kind === "group" ? r.group.id : r.spaceId));

export type Drop =
  /** Into a group (at its end). */
  | { kind: "group"; groupId: string }
  /** At the top level, before this space or group (null: the end). */
  | { kind: "top"; beforeId: string | null };

/** Whether `spaceId` may be dropped there: not into its own group, not before itself. */
export function canDrop(tree: Tree, spaceId: string, drop: Drop): boolean {
  if (drop.kind === "group") {
    const g = tree.groups.find((x) => x.id === drop.groupId);
    return Boolean(g) && !g?.spaceIds.includes(spaceId);
  }
  return drop.beforeId !== spaceId;
}

/** Moves a space into a group, out of its group, or before another top-level row. */
export function moveSpace(
  tree: Tree,
  spaceIds: readonly string[],
  spaceId: string,
  drop: Drop,
): Tree {
  if (!canDrop(tree, spaceId, drop)) return tree;
  const order = topOrder(tree, spaceIds).filter((id) => id !== spaceId);
  const groups = tree.groups.map((g) => ({
    ...g,
    spaceIds: g.spaceIds.filter((id) => id !== spaceId),
  }));
  if (drop.kind === "group") {
    return {
      order,
      groups: groups.map((g) =>
        g.id === drop.groupId ? { ...g, spaceIds: [...g.spaceIds, spaceId] } : g,
      ),
    };
  }
  const at = drop.beforeId === null ? -1 : order.indexOf(drop.beforeId);
  if (at < 0) order.push(spaceId);
  else order.splice(at, 0, spaceId);
  return { groups, order };
}

/**
 * BOARD-ORDER: a company admin moved a space in the company's shared order, so the person's own
 * top-level order of spaces gives way to it: the space leaves its group, and only the groups keep
 * their places (spaces outside groups then follow the company's order).
 */
export function followCompanyOrder(tree: Tree, spaceId: string): Tree {
  const groupIds = new Set(tree.groups.map((g) => g.id));
  return {
    groups: tree.groups.map((g) => ({ ...g, spaceIds: g.spaceIds.filter((id) => id !== spaceId) })),
    order: tree.order.filter((id) => groupIds.has(id)),
  };
}

export function addGroup(tree: Tree, spaceIds: readonly string[], name: string, id: string): Tree {
  return {
    groups: [...tree.groups, { id, name, spaceIds: [] }],
    order: [...topOrder(tree, spaceIds), id],
  };
}

export function renameGroup(tree: Tree, id: string, name: string): Tree {
  return { ...tree, groups: tree.groups.map((g) => (g.id === id ? { ...g, name } : g)) };
}

/** Deletes a group and puts its spaces back at the top level, where the group was. */
export function removeGroup(tree: Tree, spaceIds: readonly string[], id: string): Tree {
  const group = tree.groups.find((g) => g.id === id);
  const order = topOrder(tree, spaceIds);
  const at = order.indexOf(id);
  const rest = order.filter((x) => x !== id);
  rest.splice(at < 0 ? rest.length : at, 0, ...(group?.spaceIds ?? []));
  return { groups: tree.groups.filter((g) => g.id !== id), order: rest };
}

export function toggleFavorite(list: readonly Favorite[], f: Favorite): Favorite[] {
  return list.some((x) => x.kind === f.kind && x.id === f.id)
    ? list.filter((x) => !(x.kind === f.kind && x.id === f.id))
    : [...list, f];
}

// Persistence -----------------------------------------------------------------------------------

const EVENT = "companyos:work-arrangement";

function read<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode or a full store: the arrangement lives for this page only.
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: key }));
}

function useStored<T>(key: string, fallback: T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => read(key, fallback));
  // biome-ignore lint/correctness/useExhaustiveDependencies: `fallback` is a constant per call site.
  useEffect(() => {
    setValue(read(key, fallback));
    const sync = (e: Event) => {
      const changed = e instanceof StorageEvent ? e.key : (e as CustomEvent<string>).detail;
      if (changed === key) setValue(read(key, fallback));
    };
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [key]);
  const set = useCallback(
    (next: T) => {
      setValue(next);
      write(key, next);
    },
    [key],
  );
  return [value, set];
}

// WOS-BUNDLE: favorites moved to the server (favorites.ts); the old browser list migrates once.
export { useFavorites } from "./favorites";

export function useTree(companyId: string) {
  return useStored<Tree>(`companyos.work.tree.${companyId}`, EMPTY_TREE);
}
